import client from 'src/gatsby-plugin-apollo/client';
import { usePublicSession, useSession } from './usePublicSession';
import {
  useQuery,
  useMutation,
  GET_SESSION,
  GET_OPEN_ENROLLMENT_DATES,
  REPLACE_DEPENDENTS,
} from 'src/data';
import { County, getCounty } from './useZipcode';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from './useParams';
import { getDefaultExchangeType, isIchraPartner } from 'src/utils';
import { getReplacePayload, paramsDiffer } from 'src/utils/explorer';
import { ExchangeType } from 'src/data';
import { useAnonymousProfile } from 'src/utils/useAnonymousProfile';

export interface ExplorerValues {
  year?: number;
  income?: number;
  zip?: string;
  place?: County;
}

/**
 * @param root - set only by the single instance in SessionContext (see useSyncParamsToSession)
 */
export const useExplorer = ({ root = false }: { root?: boolean } = {}) => {
  const {
    loading: checkingSession,
    sessionID,
    upsertSession,
    storeNewSession,
    upserting,
  } = usePublicSession();
  const { partner } = useAnonymousProfile();

  const { loading, data } = useQuery(GET_SESSION, {
    client,
    variables: { id: sessionID },
    skip: !sessionID,
    fetchPolicy: 'cache-first',
  });

  // Coverage years come from OpenEnrollmentConfig, same as the app.
  const { loading: loadingOE, data: oeData } = useQuery(GET_OPEN_ENROLLMENT_DATES, {
    client,
    fetchPolicy: 'cache-first',
  });
  const oeDates = oeData?.reference?.health?.openEnrollmentDates;

  const [replaceDependentsMutation] = useMutation(REPLACE_DEPENDENTS, { client });

  const current = useMemo(() => {
    const session = data?.getPublicHealthExplorerData;

    return {
      ...(session || {}),
      coverageYear: session?.coverageYear || oeDates?.preferredCoverageYear,
      members: session?.deps?.length ? session.deps : (session?.dependents ?? []),
    };
  }, [data, oeDates?.preferredCoverageYear]);

  /**
   * Sets the household to exactly `applicants`. Replace drops any field we don't send,
   * so every member is sent in full, carrying lawfulPresence over from the current members.
   */
  const replaceDependents = async (explorerDataID: string, applicants) => {
    await replaceDependentsMutation({
      refetchQueries: ['PublicHealthSession', 'PublicHealthPlans'],
      awaitRefetchQueries: true,
      variables: {
        input: {
          explorerDataID,
          publicHealthExplorerDependentInputs: getReplacePayload(applicants, current.members),
        },
      },
    });
  };

  const upsertExplorer = async (values, reload = false) => {
    const existingDependents = (current.dependents || [])?.map(({ __typename, ...m }) => m);
    const existingProviders = (current.providersWanted || [])?.map(({ __typename, ...p }) => p);
    const existingDrugs = (current.drugsWanted || [])?.map(({ __typename, ...d }) => d);
    const state = values.place?.state || current.state;

    // ONLY set the default exchange type on NEW sessions, otherwise we overwrite a user's
    // current selection. ICHRA partners (e.g. Decisely) resolve to undefined here since their
    // default is pathway-dependent and pathway isn't known until the session exists — the
    // effect below applies it once the session loads.
    const defaultExchangeType = sessionID ? undefined : getDefaultExchangeType({ partner });

    const input = {
      id: sessionID,
      zipcode: values.zip || current.zipcode,
      state,
      countyfips: values.place?.fips || current.countyfips,
      coverageYear: values.year || current.coverageYear,
      income: 'income' in values ? values.income : current.income,
      dependents: existingDependents,
      exchangeType: values.exchangeType || defaultExchangeType,
      plannedUsage: values.plannedUsage,
      providersWanted: values.providersWanted || existingProviders,
      drugsWanted: values.drugsWanted || existingDrugs,
      providerPlanID: values.providerPlanID || current.providerPlanID,
    };

    if (!sessionID && !(values.zip && values.place)) {
      console.log('Unable to update or create new session', input);
      return;
    }

    const needsDependents = !!values.applicants?.length;

    const result = await upsertSession({
      refetchQueries:
        reload && !needsDependents ? ['PublicHealthPlans', 'PublicHealthSession'] : undefined,
      variables: { input },
    });

    const upsertedID = result?.data?.upsertPublicHealthExplorerData?.id;

    if (needsDependents) {
      const explorerDataID = upsertedID || sessionID;

      if (explorerDataID) {
        await replaceDependents(explorerDataID, values.applicants);
      }
    }

    // Publish a new session only now, with its household saved (E-8756).
    // No-op for an existing session.
    if (upsertedID) storeNewSession(upsertedID);

    return result;
  };

  // For ICHRA partners (e.g. Decisely) the default exchange type depends on whether the
  // consumer is in an EDE (FFM) or SBE (SBM/State App) state. We can't know that until the
  // session exists and the backend returns `pathway`, so apply the default here, once, only
  // when no exchange type has been set yet (so we never override a manual toggle). We fall
  // back to off-exchange if the pathway is somehow missing, matching the prior default.
  useEffect(() => {
    // the root instance is mounted on every page; leave this to the PX pages as before
    if (root) return;
    if (!current?.id || current.exchangeType) return;
    if (!isIchraPartner({ partner })) return;

    const next =
      getDefaultExchangeType({ partner, pathway: current.pathway }) ?? ExchangeType.OffMarket;
    upsertExplorer({ exchangeType: next }, true);
  }, [current?.id, current?.exchangeType, current?.pathway, partner]);

  return {
    loading: loading || loadingOE || checkingSession,
    data: current,
    upsertExplorer,
    upserting,

    // other
    possibleYears: [...(oeDates?.possibleCoverageYears ?? [])].sort((a, b) => b - a),
  };
};

/**
 * Applies the zip/income/year/applicants URL params to the session, once per page load.
 * Mounted once, from SessionContext, so the household is only ever written from one place
 * no matter how many useExplorer instances are on the page.
 */
export const useSyncParamsToSession = () => {
  const params = useParams();
  const { sessionID } = useSession();
  const { loading, data, upsertExplorer } = useExplorer({ root: true });

  const evaluated = useRef(false);
  const [status, setStatus] = useState<'pending' | 'syncing' | 'done'>('pending');
  const [error, setError] = useState<Error>();

  const sessionLoaded = !!sessionID && !loading && data.id === sessionID;

  useEffect(() => {
    // only make updates when zip is specified
    const zip = params.zip;
    if (!zip || !sessionLoaded || evaluated.current) return;
    evaluated.current = true;

    if (!paramsDiffer(params, data)) {
      setStatus('done');
      return;
    }

    const sync = async () => {
      setStatus('syncing');

      try {
        const vals: Record<string, unknown> = {
          zip,
          place: await getCounty(zip),
        };

        if (params.income != null) vals.income = params.income;
        if (params.applicants?.length) vals.applicants = params.applicants;
        if (params.year) vals.year = params.year;

        await upsertExplorer(vals);
      } catch (e) {
        setError(e as Error);
      } finally {
        setStatus('done');
      }
    };

    sync();
  }, [sessionLoaded]);

  return {
    syncing: !!params.zip && status !== 'done',
    syncError: error,
  };
};
